import { Modal } from "@/ui/atoms";
import { User, Mail, Building2, Shield, BadgeCheck, Flag } from "lucide-react";
import type { TUser } from "@/store/auth/sliceAuth";

export type TViewSettingsProps = {
    isOpen: boolean;
    onClose: () => void;
    user: TUser | null;
    effectiveCompanyName: string | null;
    effectiveSubgroup: string | null;
    /**
     * Opens the caller's own feature-flags modal. Only rendered for Proda
     * users: non-Proda users never see any mention of feature flags.
     */
    onOpenFeatureFlags?: () => void;
};

type TInfoRowProps = {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
};

const InfoRow = ({ icon, label, value }: TInfoRowProps) => (
    <div className="flex items-start gap-4 py-3">
        <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 shrink-0">
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                {label}
            </p>
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{value}</div>
        </div>
    </div>
);

const Badge = ({
    children,
    variant = "default",
}: {
    children: React.ReactNode;
    variant?: "default" | "blue" | "amber";
}) => {
    const variants = {
        default: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
        blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
        amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
    };
    return (
        <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}
        >
            {children}
        </span>
    );
};

export const createViewSettings = () => {
    return function ViewSettings({
        isOpen,
        onClose,
        user,
        effectiveCompanyName,
        effectiveSubgroup,
        onOpenFeatureFlags,
    }: TViewSettingsProps) {
        if (!user) return null;

        const displayCompany = effectiveCompanyName || user.companyName;

        // Determine the effective subgroup label for display
        const realSubgroup = user.subgroup;
        const activeSubgroup = effectiveSubgroup ?? realSubgroup;
        const activeSubgroupLabel = activeSubgroup
            ? (user.subgroupLabels[activeSubgroup] ?? activeSubgroup)
            : (user.roleLabels[user.role] ?? user.role);

        const realSubgroupLabel = realSubgroup
            ? (user.subgroupLabels[realSubgroup] ?? realSubgroup)
            : (user.roleLabels[user.role] ?? user.role);

        const isSubgroupSwitched = effectiveSubgroup !== null && effectiveSubgroup !== realSubgroup;

        return (
            <Modal
                open={isOpen}
                onOpenChange={onClose}
                title="Settings"
                description="Manage your preferences"
                className="max-w-2xl"
            >
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    <InfoRow
                        icon={<User className="h-4 w-4" />}
                        label="Name"
                        value={
                            <div className="flex items-center gap-2 flex-wrap">
                                <span>{user.displayName}</span>
                                {user.isProdaUser && (
                                    <Badge variant="blue">
                                        <BadgeCheck className="h-3 w-3 mr-1" />
                                        Proda
                                    </Badge>
                                )}
                            </div>
                        }
                    />
                    <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={user.email} />
                    <InfoRow
                        icon={<Building2 className="h-4 w-4" />}
                        label="Current Company"
                        value={
                            <div className="flex items-center gap-2 flex-wrap">
                                <span>{displayCompany}</span>
                                {effectiveCompanyName &&
                                    effectiveCompanyName !== user.companyName && (
                                        <Badge variant="amber">Switched</Badge>
                                    )}
                            </div>
                        }
                    />
                    <InfoRow
                        icon={<Shield className="h-4 w-4" />}
                        label="Role"
                        value={
                            <div className="flex items-center gap-2 flex-wrap">
                                <span>{activeSubgroupLabel}</span>
                                {isSubgroupSwitched && <Badge variant="amber">Switched</Badge>}
                            </div>
                        }
                    />
                    {effectiveCompanyName && effectiveCompanyName !== user.companyName && (
                        <InfoRow
                            icon={<Building2 className="h-4 w-4" />}
                            label="Home Company"
                            value={
                                <span className="text-slate-500 dark:text-slate-400">
                                    {user.companyName}
                                </span>
                            }
                        />
                    )}
                    {isSubgroupSwitched && (
                        <InfoRow
                            icon={<Shield className="h-4 w-4" />}
                            label="Home Role"
                            value={
                                <span className="text-slate-500 dark:text-slate-400">
                                    {realSubgroupLabel}
                                </span>
                            }
                        />
                    )}
                </div>
                {user.isProdaUser && onOpenFeatureFlags && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <button
                            type="button"
                            onClick={onOpenFeatureFlags}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            data-testid="settings-feature-flags"
                        >
                            <Flag className="h-4 w-4" />
                            My feature flags
                        </button>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            PRODA only: override feature flags for your own account. Your overrides
                            also apply while viewing as another company, so you can try an
                            experimental feature against its data.
                        </p>
                    </div>
                )}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">
                        ID: {user.id}
                    </p>
                </div>
            </Modal>
        );
    };
};
